/**
 * What the two editing tables decide, apart from anything that draws them.
 *
 * Patrick's sixth observation is that a TABLE reads better than a dropdown or a
 * text box, for child parts and for attributes alike. Both tables produce a
 * REQUEST rather than a picture, so every decision they make is a claim about a
 * value, and each one is exported and tested directly:
 *
 *   - a child row is a definition plus a multiplicity, and the back end takes the
 *     same rows in two places on purpose - `child_parts` on `POST /api/parts` and
 *     `children` on `POST /api/parts/{id}/children` - so ONE component serves the
 *     wizard and the pane, and one function assembles the rows for both. A row
 *     with no multiplicity is sent as a BARE STRING, because omitting it states
 *     nothing, which is the language default and is not `[1]`;
 *   - an attribute row sends a BARE NUMBER. The unit belongs to the field - the
 *     library declares `dryMass : ISQ::MassValue` and the descriptor carries
 *     `unit: "SI::kg"` - so the server composes `3.5 [SI::kg]` and nobody types a
 *     bracket. This is also the fix for the defect that shipped: writing the
 *     value as a quoted string produced `:>> dryMass = "3.5 [SI::kg]";`, which
 *     re-parses cleanly and is wrong in the exported text and nowhere else.
 *
 * The two rules worth stating out loud, because both are easy to get plausibly
 * wrong and neither is visible on screen afterwards:
 *
 *   - `''` CLEARS a value, and unset is not zero. A part genuinely drawing 0 W is
 *     a different statement from nobody having filled it in, and the rollup
 *     depends on the difference: an unset value makes every total above it
 *     partial, while a stated zero is a figure. So a cleared box sends `''` and
 *     never `'0'`;
 *   - only CHANGED fields are sent. The API treats an omitted field as "leave
 *     alone" and an empty one as "clear", so sending every field on every save
 *     would rewrite values nobody touched - and would clear the ones this pane
 *     shows as blank because they were never stated.
 *
 * A value stored in another unit is the one case where a bare number would lie.
 * `250 [SI::g]` is legal in a hand-authored file, and re-sending `250` would
 * restate it as 250 kg - a thousand-fold error that reads as no edit at all. Such
 * a row keeps the unit it was written with and sends the whole quantity, which
 * the API accepts, and says so on screen.
 */

// From the parser module itself, not through FieldControl: it lived there for an
// afternoon and a re-export bridge was left behind so the tree would build. One
// quantity parser, imported directly, is the point of that module - a second
// copy of the rule would disagree with the first invisibly.
import { splitQuantity } from '../entity/quantity'
import type { ChildPartRow, CreatedPart, RollingAttribute } from '../../schema'
import { shortUnit } from './rollup-model'
import type { WritePermission } from './parts-model'

// ---------------------------------------------------------------------------
// Child parts
// ---------------------------------------------------------------------------

export const CHILD_TABLE_LABEL = 'Child parts to add'

export const MULTIPLICITY_HELP =
  'How many of it, e.g. [4] or [1..*]. Left empty the model states nothing, which ' +
  'is the language default and is NOT the same as one.'

export const MULTIPLICITY_SHAPE =
  'A multiplicity is [4], [0..1], [1..*] or [*] — a bound, or two separated by “..”.'

export const NEEDS_A_DEFINITION =
  'One row names a multiplicity but no part. A row has to say what it is a ' +
  'multiplicity OF; the server refuses it rather than skipping it.'

export const NEEDS_A_ROW = 'Choose at least one part to declare inside this one.'

/**
 * One row somebody is filling in.
 *
 * `key` exists because a row is identified by nothing else until it has a
 * definition: two blank rows are two rows, and keying on the definition would
 * make React fold them together.
 */
export interface ChildDraft {
  key: string
  /** The part def's ID, as the picker reports it. */
  definition: string
  /** As typed. Normalised on the way out rather than on every keystroke. */
  multiplicity: string
}

/** A blank row. */
export function emptyChildDraft(key: string): ChildDraft {
  return { key, definition: '', multiplicity: '' }
}

/**
 * A multiplicity in the form that reads back.
 *
 * `4`, `1..*` and `*` are all accepted by the API - `set_multiplicity` is
 * tolerant - but `[4]` is what a later GET answers, so the brackets are added
 * here and nobody has to type them to get a form that agrees with itself.
 *
 * @param text as typed
 * @returns the bracketed form, or '' for nothing stated
 */
export function normalizeMultiplicity(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return ''
  const inner = trimmed.startsWith('[') && trimmed.endsWith(']')
    ? trimmed.slice(1, -1).trim()
    : trimmed
  return inner ? `[${inner}]` : ''
}

/**
 * Why a multiplicity cannot be sent, or '' when it can.
 *
 * Shape only, and deliberately not a judgement about whether the bounds make
 * sense: `[0..0]` is odd and legal, and a form that forbids what the server
 * accepts eventually forbids something somebody needs.
 *
 * @param text as typed
 */
export function multiplicityRefusal(text: string): string {
  const bracketed = normalizeMultiplicity(text)
  if (!bracketed) return ''
  const inner = bracketed.slice(1, -1)
  return /^(\*|\d+)(\.\.(\*|\d+))?$/.test(inner) ? '' : MULTIPLICITY_SHAPE
}

/**
 * The rows the API takes, from the rows somebody filled in.
 *
 * A row with no definition is DROPPED rather than sent: a table always has a
 * blank row on the end to type into, and sending it would be a 422 for a row
 * nobody meant. A row that names a multiplicity and no definition is a different
 * thing - somebody meant it and left it half done - and `childRowsRefusal` says
 * so rather than this function quietly discarding it.
 *
 * The bare string form is used whenever there is no multiplicity, because that is
 * what "states nothing" looks like on the wire.
 *
 * @param drafts the rows as filled in
 */
export function childPartRows(drafts: readonly ChildDraft[]): ChildPartRow[] {
  const rows: ChildPartRow[] = []
  for (const draft of drafts) {
    const definition = draft.definition.trim()
    if (!definition) continue
    const multiplicity = normalizeMultiplicity(draft.multiplicity)
    rows.push(multiplicity ? { definition, multiplicity } : definition)
  }
  return rows
}

/**
 * Why the child rows cannot be sent yet, or '' when they can.
 *
 * Permission first, for the reason it always comes first: somebody who may not
 * write at all is not helped by being told which row is incomplete.
 *
 * @param drafts the rows as filled in
 * @param permission what `useCanWrite()` answered
 */
export function childRowsRefusal(
  drafts: readonly ChildDraft[],
  permission: WritePermission,
): string {
  if (!permission.allowed) return permission.reason
  const problem = childRowsProblem(drafts)
  if (problem) return problem
  if (childPartRows(drafts).length === 0) return NEEDS_A_ROW
  return ''
}

/**
 * What is WRONG with the rows, ignoring whether there are any.
 *
 * Its own function because the two callers differ on exactly that point: the
 * add-a-child dialog exists to add children and refuses an empty table, while the
 * wizard's children are optional and a definition with nothing inside it is a
 * perfectly good thing to create. Both refuse a half-filled row.
 *
 * @param drafts the rows as filled in
 */
export function childRowsProblem(drafts: readonly ChildDraft[]): string {
  if (drafts.some((draft) => !draft.definition.trim() && draft.multiplicity.trim())) {
    return NEEDS_A_DEFINITION
  }
  for (const draft of drafts) {
    if (!draft.definition.trim()) continue
    const refusal = multiplicityRefusal(draft.multiplicity)
    if (refusal) return refusal
  }
  return ''
}

/**
 * What adding children actually made, in the names the SERVER chose.
 *
 * Worth saying rather than reporting a count: a usage carries no `<ID>` and is
 * named after its definition with a lowered first letter, made unique MODEL-WIDE,
 * so adding a second Structure produces `structure2` - a name nobody typed and
 * somebody will later search for. Every name is read off the response.
 *
 * @param result the answer from `POST /api/parts/{id}/children`
 */
export function addedSummary(result: CreatedPart): string {
  const usages = (result.usages ?? []).map((usage) => usage.name)
  if (!usages.length) return `${result.identifier} unchanged`
  return `${result.identifier} now contains ${usages.join(', ')}`
}

/**
 * What a save actually wrote, in the SERVER'S words.
 *
 * The change summary reports the composed quantity rather than the request:
 * `PATCH` with `3.5` answers `{"changed": {"dry_mass": "3.5 [SI::kg]"}}`, which is
 * what the next `GET` will say. So the toast quotes it - that is the proof the unit
 * was composed by the server rather than typed by anybody - and a cleared field is
 * reported as CLEARED, because an empty string in a list of new values reads as
 * nothing having happened.
 *
 * @param identifier the part, as the answer names it
 * @param changed `changed` from the PATCH answer
 */
export function savedSummary(identifier: string, changed: Record<string, unknown>): string {
  const parts = Object.entries(changed).map(([name, value]) => {
    const text = value === null || value === undefined ? '' : String(value)
    return text ? `${name} ${text}` : `${name} cleared`
  })
  if (!parts.length) return `${identifier} unchanged`
  return `${identifier}: ${parts.join(', ')}`
}

// ---------------------------------------------------------------------------
// Attributes
// ---------------------------------------------------------------------------

/**
 * The section heading, and what it is called matters.
 *
 * 'Quantities this part states' described the contents and read as a sentence
 * fragment beside two other headings that were noun phrases. These are the
 * part's OWN attributes - the values somebody writes down here - as against the
 * rollup's, which are computed from everything underneath.
 */
export const ATTRIBUTE_TABLE_LABEL = 'Part Attributes'

export const ATTRIBUTE_NOTE =
  'One row per quantity the model declares — read from the payload, so a fourth ' +
  'one added to the profile appears here with no change to this screen. Type a ' +
  'NUMBER; the unit belongs to the attribute and is added for you.'

export const CLEARING_NOTE =
  'An empty box clears the value, which is UNSET and not zero: a part drawing 0 W ' +
  'is a different statement from nobody having filled it in, and every total above ' +
  'an unset value is reported as partial rather than as a figure.'

export const USAGE_STATES_NOTHING =
  'A usage states no quantities of its own. The rollup refuses a value stated on ' +
  'one — state it on the definition, or specialize the definition — so there is ' +
  'nothing here to edit.'

/** The number grammar the API states, exactly: sign, digits, point, exponent. */
const NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/

/** Whether what somebody typed is a number the API will take. */
export function isNumberShaped(text: string): boolean {
  return NUMBER.test(text.trim())
}

/**
 * One row of the attributes table: a declared quantity, and what it says.
 *
 * `value` is whatever the quantity control last handed back, which is already the
 * thing to send - a bare number, `''` to clear, or a whole quantity in the unit it
 * was stated in. The control lives in `components/entity/FieldControl`, where the
 * generated forms use it too; a second one here would be a second set of rules
 * about a value the model is sensitive to.
 */
export interface AttributeDraft {
  /** The column and field name, e.g. `dry_mass`. */
  name: string
  /** The profile feature it redefines, e.g. `dryMass`. Shown, never sent. */
  feature: string
  label: string
  /** The unit the FIELD is stated in, from the descriptor. Shown, never typed. */
  unit: string
  value: unknown
}

/**
 * One row per quantity the model declares, from the payload.
 *
 * `attributes` on the detail is the authority for WHICH rows there are and what
 * unit each is in - the model's own statement, via `@EchelonSysML::RollsUp` - so a
 * program that declares a fourth quantity gets a row with nothing here being
 * touched. That is the whole reason the marker lives in the model, and why this
 * is a fixed list of the declared quantities rather than free-form rows.
 *
 * @param attributes `attributes` from `GET /api/parts/{id}`
 * @param stated the part's stored values, i.e. the detail itself
 */
export function attributeDrafts(
  attributes: readonly RollingAttribute[],
  stated: Record<string, unknown>,
  /**
   * The attributes this part may state, from `statable` on the payload.
   *
   * An attribute belongs to the part def that DECLARES it, so a fastener has no
   * power generation and asking it for one is a question with no answer. It was
   * asked anyway - every rolling quantity, on every part - which is the same
   * defect the create form had.
   *
   * Undefined offers everything, which is what an older server's payload gets:
   * too many rows beats a missing one.
   */
  statable?: readonly string[],
): AttributeDraft[] {
  const allowed = statable ? new Set(statable) : null
  return attributes
    .filter((attribute) => !allowed || allowed.has(attribute.feature))
    .map((attribute) => ({
    name: attribute.name,
    feature: attribute.feature,
    label: attribute.label,
    unit: attribute.unit,
    value: stated[attribute.name] ?? '',
  }))
}

/**
 * Whether two quantities say the same thing, however each is written.
 *
 * This is what keeps a save down to the fields somebody actually edited. The read
 * side and the write side are DIFFERENT SHAPES - `GET` answers `"40 [SI::kg]"` and
 * a form holds `40` - so comparing them as text reports every row as changed, and
 * a save would then rewrite three quantities to edit one.
 *
 * A missing unit means the field's own, which is the whole convention: a bare
 * number is a value in the unit the attribute declares.
 *
 * @param unit the unit the field declares, e.g. `SI::kg`
 */
export function sameQuantity(left: unknown, right: unknown, unit: string): boolean {
  const a = splitQuantity(left)
  const b = splitQuantity(right)
  if (shortUnit(a.unit || unit) !== shortUnit(b.unit || unit)) return false
  const one = a.number.trim()
  const two = b.number.trim()
  if (one === two) return true
  // Numerically when both are numbers, so `40` and `40.0` are one value; as text
  // otherwise, because half-typed input is not a number and must not compare
  // equal to anything by rounding.
  if (!isNumberShaped(one) || !isNumberShaped(two)) return false
  return Number(one) === Number(two)
}

/**
 * Only what changed, as `api.update` should be given it.
 *
 * Every omitted field is left alone and every empty one is CLEARED, so the
 * difference between the two decides whether a save edits one quantity or
 * silently unsets the other two. A cleared box sends `''` - unset, and never
 * `'0'`, which would state that the part draws no power at all.
 *
 * @param drafts the rows as edited
 * @param stated the part's stored values, i.e. the detail as fetched
 */
export function attributeChanges(
  drafts: readonly AttributeDraft[],
  stated: Record<string, unknown>,
): Record<string, unknown> {
  const changes: Record<string, unknown> = {}
  for (const draft of drafts) {
    const before = stated[draft.name] ?? ''
    if (sameQuantity(draft.value, before, draft.unit)) continue
    const { number } = splitQuantity(draft.value)
    // '' rather than the control's own value, so that a box somebody emptied
    // cannot arrive as '0', as ' ' or as null - three ways of not saying "unset".
    changes[draft.name] = number.trim() ? draft.value : ''
  }
  return changes
}

/**
 * Why the attributes cannot be saved yet, or '' when they can.
 *
 * The shape is refused HERE rather than at the rollup, which is the point of the
 * quantity kind: `about 250 kg` is caught at entry instead of turning up later as
 * a value that could not be summed. Half-typed text is refused for the same
 * reason - `3.` and `1e` are on the way to a number and are not one yet.
 *
 * @param drafts the rows as edited
 * @param permission what `useCanWrite()` answered
 */
export function attributeRefusal(
  drafts: readonly AttributeDraft[],
  permission: WritePermission,
): string {
  if (!permission.allowed) return permission.reason
  for (const draft of drafts) {
    const { number, unit } = splitQuantity(draft.value)
    const value = number.trim()
    if (!value) continue
    if (!isNumberShaped(value)) {
      return (
        `${draft.label} takes a number in ${shortUnit(unit || draft.unit)} — ` +
        `“${value}” is not one, and the unit is added for you.`
      )
    }
  }
  return ''
}
